import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PrintIcon from "@mui/icons-material/Print";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import BlockIcon from "@mui/icons-material/Block";
import RefreshIcon from "@mui/icons-material/Refresh";
import { API_URL, API_UPLOAD_URL } from "./config";
import IdCardPreview from "./IdCardPreview";

const ROWS_PER_PAGE = 200;

const IssuedIdCards = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";
  const BLUE = "rgba(25,118,210,0.95)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };
  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: BLUE,
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };
  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
    "&.MuiCheckbox-indeterminate": { color: THEME_ORANGE },
  };
  const checkboxAllSx = {
    color: "#fff",
    "&.Mui-checked": { color: "#fff" },
    "&.MuiCheckbox-indeterminate": { color: "#fff" },
  };
  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };
  const theadRowSx = {
    backgroundColor: BLUE,
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };
  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const token = localStorage.getItem("auth");
  const printFrameRef = useRef(null);

  const [logincampusCode, setLogincampusCode] = useState("");
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const [cardType, setCardType] = useState("");
  const [status, setStatus] = useState("READY_FOR_PRINT");

  const [subCampusCode, setSubCampusCode] = useState([]);
  const [degreeCode, setDegreeCode] = useState([]);
  const [branchCode, setBranchCode] = useState([]);
  const [semesterCode, setSemesterCode] = useState([]);

  const [searchText, setSearchText] = useState("");

  const [printSide, setPrintSide] = useState("BOTH"); // FRONT | BACK | BOTH

  const [lookups, setLookups] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const [page, setPage] = useState(0);

  const [error, setError] = useState("");
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const cc =
        payload?.logincampuscode ||
        payload?.campusCode ||
        payload?.CampusCode ||
        "";
      setLogincampusCode(cc);
    } catch {
      setError("Failed to decode auth token");
    }
  }, [token]);

  const authConfig = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token]
  );

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedIds.includes(r.CardIssueId)),
    [rows, selectedIds]
  );

  const pagedRows = useMemo(() => {
    const start = page * ROWS_PER_PAGE;
    return rows.slice(start, start + ROWS_PER_PAGE);
  }, [rows, page]);

  const allRowsOnPageSelected =
    pagedRows.length > 0 &&
    pagedRows.every((r) => selectedIds.includes(r.CardIssueId));

  const someRowsOnPageSelected =
    pagedRows.some((r) => selectedIds.includes(r.CardIssueId)) &&
    !allRowsOnPageSelected;

  const canMarkIssued =
    selectedRows.length > 0 &&
    selectedRows.every((r) => String(r.Status || "").toUpperCase() === "PRINTED");

  const canMarkInactive =
    selectedRows.length > 0 &&
    selectedRows.every((r) => {
      const s = String(r.Status || "").toUpperCase();
      return s !== "INACTIVE" && s !== "CANCELLED";
    });

const normalizeHtmlImageSrc = (html) => {
  if (!html) return html;

  return String(html).replace(/src="([^"]+)"/g, (match, src) => {
    if (!src) return match;

    const value = String(src).trim();

    if (value.startsWith("data:")) return match;
    if (/^https?:\/\//i.test(value)) return match;

    const clean = value.replace(/^\/+/, "");

    if (clean.startsWith("uploads/")) {
      return `src="${API_UPLOAD_URL}/${clean}"`;
    }

    return `src="${API_UPLOAD_URL}/uploads/${clean}"`;
  });
};

  const joinParam = (arr) =>
    Array.isArray(arr) && arr.length ? arr.join(",") : "";

  const loadLookups = async () => {
    try {
      if (!logincampusCode) return;

      const response = await axios.get(`${API_URL}/api/lookups/all`, {
        ...authConfig,
        params: { CampusCode: logincampusCode },
      });

      setLookups({
        SubCampusCode: response.data?.SubCampusCode || [],
        DegreeCode: response.data?.DegreeCode || [],
        BranchCode: response.data?.BranchCode || [],
        SemesterCode: (response.data?.SemesterCode || []).map(String),
      });
    } catch (err) {
      console.error("Failed to load lookups", err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${API_URL}/api/idcards/issued`, {
        ...authConfig,
        params: {
          CampusCode: logincampusCode,
          cardType,
          status,
          SubCampusCode: joinParam(subCampusCode),
          DegreeCode: joinParam(degreeCode),
          BranchCode: joinParam(branchCode),
          SemesterCode: joinParam(semesterCode),
          search: searchText.trim(),
        },
      });

      setRows(response.data || []);
      setSelectedIds([]);
      setPage(0);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load issued cards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!logincampusCode) return;
    loadLookups();
  }, [logincampusCode]);

  useEffect(() => {
    if (!logincampusCode) return;
    loadData();
  }, [logincampusCode, status]);

  const toggleSelectOne = (cardIssueId) => {
    setSelectedIds((prev) =>
      prev.includes(cardIssueId)
        ? prev.filter((id) => id !== cardIssueId)
        : [...prev, cardIssueId]
    );
  };

  const toggleSelectAllOnPage = () => {
    const pageIds = pagedRows.map((r) => r.CardIssueId);

    if (allRowsOnPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      return;
    }

    setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const markIssuedSelected = async () => {
    try {
      if (!selectedIds.length) {
        setError("Select at least one card");
        return;
      }

      if (!canMarkIssued) {
        setError("Only PRINTED cards can be marked as ISSUED");
        return;
      }

      setBulkActionLoading(true);

      await axios.post(
        `${API_URL}/api/idcards/issued/bulk-issue-mark`,
        {
          CampusCode: logincampusCode,
          CardIssueIds: selectedIds,
        },
        authConfig
      );

      setSnack({
        open: true,
        message: "Selected cards marked as ISSUED",
        severity: "success",
      });

      setSelectedIds([]);
      await loadData();
    } catch (err) {
      setError(
        err?.response?.data?.error || "Failed to mark selected cards as issued"
      );
    } finally {
      setBulkActionLoading(false);
    }
  };

  const markInactiveSelected = async () => {
    try {
      if (!selectedIds.length) {
        setError("Select at least one card");
        return;
      }

      if (!canMarkInactive) {
        setError("Cancelled or already inactive cards cannot be marked inactive again");
        return;
      }

      setBulkActionLoading(true);

      await axios.post(
        `${API_URL}/api/idcards/issued/bulk-inactive-mark`,
        {
          CampusCode: logincampusCode,
          CardIssueIds: selectedIds,
        },
        authConfig
      );

      setSnack({
        open: true,
        message: "Selected cards marked as INACTIVE",
        severity: "success",
      });

      setSelectedIds([]);
      await loadData();
    } catch (err) {
      setError(
        err?.response?.data?.error || "Failed to mark selected cards as inactive"
      );
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handlePreview = async (row) => {
    try {
      setPreviewLoading(true);

      const endpoint =
        row.CardType === "EMPLOYEE"
          ? `${API_URL}/api/idcards/preview/employee/${row.EmployeeKey}`
          : `${API_URL}/api/idcards/preview/student/${row.StudentKey}`;

      const response = await axios.post(
        endpoint,
        {
          CampusCode: logincampusCode,
          TemplateId: row.TemplateId,
        },
        authConfig
      );

      const result = response.data;

      setPreviewData({
        //frontHtml: normalizeHtmlImageSrc(result.frontHtml),
        //backHtml: normalizeHtmlImageSrc(result.backHtml),
        frontHtml: result.frontHtml,
        backHtml: result.backHtml,
        orientation:
          result?.orientation ||
          result?.Orientation ||
          result?.template?.Orientation ||
          "PORTRAIT",
        cardWidth:
          Number(result?.cardWidth) ||
          Number(result?.template?.FrontLayoutJson?.cardWidth) ||
          Number(result?.template?.BackLayoutJson?.cardWidth) ||
          Number(result?.template?.CardWidth) ||
          null,
        cardHeight:
          Number(result?.cardHeight) ||
          Number(result?.template?.FrontLayoutJson?.cardHeight) ||
          Number(result?.template?.BackLayoutJson?.cardHeight) ||
          Number(result?.template?.CardHeight) ||
          null,
      });

      setPreviewOpen(true);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

const buildPrintHtml = (cards, sideMode = "BOTH") => {
  const normalizeHtml = (html) => String(html || "");

  const getPagesForCard = (row) => {
    const pages = [];

    const frontHtml = normalizeHtml(row.frontHtml || row.FrontHtml);
    const backHtml = normalizeHtml(row.backHtml || row.BackHtml);

    if ((sideMode === "FRONT" || sideMode === "BOTH") && frontHtml.trim()) {
      pages.push(`
        <div class="print-page">
          <div class="side-label">FRONT - ${row.CardSerialNo || ""}</div>
          <div class="card-wrap">${frontHtml}</div>
        </div>
      `);
    }

    if ((sideMode === "BACK" || sideMode === "BOTH") && backHtml.trim()) {
      pages.push(`
        <div class="print-page">
          <div class="side-label">BACK - ${row.CardSerialNo || ""}</div>
          <div class="card-wrap">${backHtml}</div>
        </div>
      `);
    }

    return pages.join("");
  };

  const items = cards.map(getPagesForCard).join("");

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Print ID Cards</title>
        <style>
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          html, body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            font-family: Arial, Helvetica, sans-serif;
          }

          .print-root {
            width: 100%;
          }

          .print-page {
            width: 100%;
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            page-break-after: always;
            break-after: page;
            padding: 10mm;
          }

          .print-page {
            width: 100%;
            height: auto;
            display: flex;
            align-items: center;
            justify-content: center;
            page-break-after: always;
            break-after: page;
            padding: 0;
            margin: 0;
          }
          
          .print-page:last-of-type {
            page-break-after: auto !important;
            break-after: auto !important;
          }

          .side-label {
            font-size: 11px;
            font-weight: 700;
            color: #444;
            margin-bottom: 8px;
          }

          .card-wrap {
            display: inline-block;
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .card-wrap img {
            max-width: 100%;
            display: block;
          }

          @page {
            size: auto;
            margin: 0;
          }

          @media print {
            .side-label {
              display: none;
            }

            .print-page {
              min-height: 100vh;
              page-break-after: always;
              break-after: page;
            }

            .print-page:last-child {
              page-break-after: auto;
              break-after: auto;
            }
          }
        </style>
      </head>

      <body>
        <div class="print-root">
          ${items || "<div>No printable cards found</div>"}
        </div>
      </body>
    </html>
  `;
};

const waitForPrintWindowReady = async (printWindow) => {
  await new Promise((resolve) => {
    const done = () => setTimeout(resolve, 600);

    if (printWindow.document.readyState === "complete") {
      done();
      return;
    }

    printWindow.onload = done;
  });

  const images = Array.from(printWindow.document.images || []);
  if (!images.length) return;

  await Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) return resolve();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          setTimeout(resolve, 1500);
        })
    )
  );

  await new Promise((r) => setTimeout(r, 300));
};

const printSelected = async () => {
  try {
    if (!selectedRows.length) {
      setError("Select at least one card");
      return;
    }

    const printableIds = selectedRows
      .filter((r) =>
        ["READY_FOR_PRINT", "PRINTED", "REPRINTED"].includes(
          String(r.Status || "").toUpperCase()
        )
      )
      .map((r) => r.CardIssueId);

    if (!printableIds.length) {
      setError("Only READY_FOR_PRINT / PRINTED / REPRINTED cards can be printed");
      return;
    }

    setBulkActionLoading(true);

    const printDataRes = await axios.post(
      `${API_URL}/api/idcards/issued/print-data`,
      {
        CampusCode: logincampusCode,
        CardIssueIds: printableIds,
      },
      authConfig
    );

    const printableCards = Array.isArray(printDataRes.data)
    ? printDataRes.data.filter(
        (r) =>
          String(r.frontHtml || r.FrontHtml || "").trim() ||
          String(r.backHtml || r.BackHtml || "").trim()
      )
    : [];

    if (!printableCards.length) {
      setError("No printable ID card HTML found for selected records");
      setBulkActionLoading(false);
      return;
    }

    const printWindow = window.open("", "_blank", "width=1400,height=900");

    if (!printWindow) {
      setError("Popup blocked. Please allow popups for printing.");
      setBulkActionLoading(false);
      return;
    }

    const html = buildPrintHtml(printableCards, printSide);

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    await waitForPrintWindowReady(printWindow);

    printWindow.focus();
    printWindow.onafterprint = async () => {
      try {
        await axios.post(
          `${API_URL}/api/idcards/issued/bulk-print-mark`,
          {
            CampusCode: logincampusCode,
            CardIssueIds: printableIds,
          },
          authConfig
        );
      
        await loadData();
      } catch (e) {
        console.error(e);
      }
    };

    setSnack({
      open: true,
      message: "Selected cards printed and marked as PRINTED",
      severity: "success",
    });

    setSelectedIds([]);
    await loadData();
  } catch (err) {
    setError(err?.response?.data?.error || "Failed to print selected cards");
  } finally {
    setBulkActionLoading(false);
  }
};  

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Issued ID Cards
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Bulk print, preview, issue, and inactivate ID cards
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              sx={{
                ...outlinedBtnSx,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.45)",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.10)",
                },
              }}
              onClick={loadData}
            >
              Refresh
            </Button>
          </Stack>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {!!snack.open && (
            <Snackbar
              open
              autoHideDuration={3000}
              onClose={() => setSnack((s) => ({ ...s, open: false }))}
            >
              <Alert severity={snack.severity} sx={{ borderRadius: 2 }}>
                {snack.message}
              </Alert>
            </Snackbar>
          )}

          <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  label="Card Type"
                  value={cardType}
                  onChange={(e) => setCardType(e.target.value)}
                  fullWidth
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="STUDENT">Student</MenuItem>
                  <MenuItem value="EMPLOYEE">Employee</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  fullWidth
                  size="small"
                  sx={inputSx}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="READY_FOR_PRINT">Ready For Print</MenuItem>
                  <MenuItem value="PRINTED">Printed</MenuItem>
                  <MenuItem value="ISSUED">Issued</MenuItem>
                  <MenuItem value="REPRINTED">Reprinted</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <Autocomplete
                  multiple
                  options={lookups.SubCampusCode}
                  value={subCampusCode}
                  onChange={(_, value) => setSubCampusCode(value)}
                  disableCloseOnSelect
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField {...params} label="SubCampus" size="small" sx={inputSx} />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Autocomplete
                  multiple
                  options={lookups.DegreeCode}
                  value={degreeCode}
                  onChange={(_, value) => setDegreeCode(value)}
                  disableCloseOnSelect
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField {...params} label="Degree" size="small" sx={inputSx} />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Autocomplete
                  multiple
                  options={lookups.BranchCode}
                  value={branchCode}
                  onChange={(_, value) => setBranchCode(value)}
                  disableCloseOnSelect
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField {...params} label="Branch" size="small" sx={inputSx} />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Autocomplete
                  multiple
                  options={lookups.SemesterCode}
                  value={semesterCode}
                  onChange={(_, value) => setSemesterCode(value)}
                  disableCloseOnSelect
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField {...params} label="Semester" size="small" sx={inputSx} />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Search ID / Name / Serial No"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  fullWidth
                  size="small"
                  sx={inputSx}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") loadData();
                  }}
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    variant="contained"
                    sx={primaryBtnSx}
                    onClick={loadData}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Search"}
                  </Button>

                  <TextField
                    select
                    label="Print Side"
                    value={printSide}
                    onChange={(e) => setPrintSide(e.target.value)}
                    size="small"
                    sx={{ ...inputSx, minWidth: 150 }}
                  >
                    <MenuItem value="FRONT">Front Only</MenuItem>
                    <MenuItem value="BACK">Back Only</MenuItem>
                    <MenuItem value="BOTH">Both</MenuItem>
                  </TextField>

                  <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    sx={primaryBtnSx}
                    onClick={printSelected}
                    disabled={!selectedIds.length || bulkActionLoading}
                  >
                    Print Selected
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<LocalShippingIcon />}
                    sx={outlinedBtnSx}
                    onClick={markIssuedSelected}
                    disabled={!canMarkIssued || bulkActionLoading}
                  >
                    Mark Issued
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<BlockIcon />}
                    sx={outlinedBtnSx}
                    onClick={markInactiveSelected}
                    disabled={!canMarkInactive || bulkActionLoading}
                  >
                    Mark Inactive
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            {!!selectedIds.length && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  background: "rgba(240,120,40,0.06)",
                  border: "1px solid rgba(240,120,40,0.20)",
                }}
              >
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                  Selected: {selectedIds.length} card(s)
                </Typography>
                {!canMarkIssued && (
                  <Typography sx={{ mt: 0.5, color: BLUE }}>
                    Mark Issued is enabled only when all selected cards are in PRINTED status.
                  </Typography>
                )}
              </Box>
            )}
          </Paper>

          <Paper elevation={0} sx={tableWrapSx}>
            <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
              <TableContainer component="div">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allRowsOnPageSelected}
                          indeterminate={someRowsOnPageSelected}
                          onChange={toggleSelectAllOnPage}
                          sx={checkboxAllSx}
                        />
                      </TableCell>
                      <TableCell>Serial No</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>SubCampus</TableCell>
                      <TableCell>Degree</TableCell>
                      <TableCell>Branch</TableCell>
                      <TableCell>Semester</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Issue Date</TableCell>
                      <TableCell>Valid Upto</TableCell>
                      <TableCell>Preview</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {pagedRows.map((r, idx) => (
                      <TableRow key={r.CardIssueId} sx={zebraRowSx(idx)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.includes(r.CardIssueId)}
                            onChange={() => toggleSelectOne(r.CardIssueId)}
                            sx={checkboxSx}
                          />
                        </TableCell>
                        <TableCell>{r.CardSerialNo}</TableCell>
                        <TableCell>{r.CardType}</TableCell>
                        <TableCell>{r.StudentID || r.EmployeeID || "-"}</TableCell>
                        <TableCell>{r.FullName || r.EmployeeName || "-"}</TableCell>
                        <TableCell>{r.SubCampusCode || "-"}</TableCell>
                        <TableCell>{r.DegreeCode || "-"}</TableCell>
                        <TableCell>{r.BranchCode || "-"}</TableCell>
                        <TableCell>{r.SemesterCode || "-"}</TableCell>
                        <TableCell>{r.Status}</TableCell>
                        <TableCell>{String(r.IssueDate || "").slice(0, 10)}</TableCell>
                        <TableCell>{String(r.ValidUpto || "").slice(0, 10)}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handlePreview(r)}
                            sx={outlinedBtnSx}
                            disabled={previewLoading}
                          >
                            Preview
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}

                    {!pagedRows.length && (
                      <TableRow>
                        <TableCell colSpan={13} align="center">
                          No issued cards found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={ROWS_PER_PAGE}
              rowsPerPageOptions={[ROWS_PER_PAGE]}
            />
          </Paper>

          <Dialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            maxWidth="xl"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 900, color: BLUE }}>
              ID Card Preview
            </DialogTitle>
            <DialogContent dividers>
              {previewData ? (
                <IdCardPreview
                  title="Issued Card Preview"
                  frontHtml={previewData.frontHtml}
                  backHtml={previewData.backHtml}
                  orientation={previewData.orientation || "PORTRAIT"}
                  cardWidth={previewData.cardWidth}
                  cardHeight={previewData.cardHeight}
                />
              ) : (
                <Typography>No preview available</Typography>
              )}
            </DialogContent>
          </Dialog>
        </Box>
      </Paper>
    </Box>
  );
};

export default IssuedIdCards;